import type { SmsAgentOpsCounts, SmsAgentOpsDailyCounts, SmsWorkflowRow } from 'app/types/sms-agent-ops'

/** An SMS Agent location is silent when the AI has not replied for this long. */
export const SILENT_THRESHOLD_HOURS = 48
export const SILENT_THRESHOLD_LABEL = `48h`

export const EMPTY_COUNTS: SmsAgentOpsCounts = {
  inbound: 0,
  outbound: 0,
  agentTexts: 0,
  workflowTexts: 0,
  staffTexts: 0,
  externalTexts: 0,
  voiceAgentTexts: 0,
  inboundThreads: 0,
  unansweredThreads: 0,
  aiConversations: 0,
  repliedConversations: 0,
  bookings: 0,
  takeovers: 0,
  staffReplyTakeovers: 0,
  manualToggleTakeovers: 0,
  optOutTakeovers: 0,
  failedTexts: 0,
  undeliveredTexts: 0,
  carrierFailedTexts: 0,
  noConsentTexts: 0,
  quietHoursTexts: 0,
  stuckPendingTexts: 0,
  optOuts: 0,
  aiTurns: 0,
  toolErrorTurns: 0,
  aiLatencyMsTotal: 0,
  aiCostUsd: 0,
  staffResponses: 0,
  staffResponseMinutesTotal: 0,
  previousAiConversations: 0,
  previousTakeovers: 0,
  previousInboundThreads: 0,
  previousUnansweredThreads: 0,
}

const COUNT_KEYS = Object.keys(EMPTY_COUNTS) as (keyof SmsAgentOpsCounts)[]

/** Daily fields the charts sum; the stable `emptyTotals` for `OpsTrendGraph`. */
export const EMPTY_DAILY_TOTALS: Omit<SmsAgentOpsDailyCounts, `date`> = {
  inbound: 0,
  agentTexts: 0,
  workflowTexts: 0,
  staffTexts: 0,
  otherTexts: 0,
  staffReplyTakeovers: 0,
  manualToggleTakeovers: 0,
  optOutTakeovers: 0,
  undeliveredTexts: 0,
  carrierFailedTexts: 0,
  noConsentTexts: 0,
  quietHoursTexts: 0,
  stuckPendingTexts: 0,
  optOuts: 0,
}

export type SmsDailyField = keyof typeof EMPTY_DAILY_TOTALS

export type SmsAgentOpsRates = {
  takeoverRate?: number
  previousTakeoverRate?: number
  /** In points; undefined unless both periods had AI conversations. */
  takeoverRateChange?: number
  replyRate?: number
  bookingRate?: number
  failureRate?: number
  unansweredRate?: number
  previousUnansweredRate?: number
  unansweredRateChange?: number
  toolErrorRate?: number
  avgAiLatencySeconds?: number
  costPerConversation?: number
  avgStaffResponseMinutes?: number
}

const percent = (numerator: number, denominator: number) => (denominator ? (numerator / denominator) * 100 : undefined)

const change = (current?: number, previous?: number) =>
  current === undefined || previous === undefined ? undefined : current - previous

/** Rates from counts; undefined (rendered `-`) when the denominator is zero. */
export function ratesFor(counts: SmsAgentOpsCounts): SmsAgentOpsRates {
  const takeoverRate = percent(counts.takeovers, counts.aiConversations)
  const previousTakeoverRate = percent(counts.previousTakeovers, counts.previousAiConversations)
  const unansweredRate = percent(counts.unansweredThreads, counts.inboundThreads)
  const previousUnansweredRate = percent(counts.previousUnansweredThreads, counts.previousInboundThreads)
  return {
    takeoverRate,
    previousTakeoverRate,
    takeoverRateChange: change(takeoverRate, previousTakeoverRate),
    replyRate: percent(counts.repliedConversations, counts.aiConversations),
    bookingRate: percent(counts.bookings, counts.aiConversations),
    failureRate: percent(counts.failedTexts, counts.outbound),
    unansweredRate,
    previousUnansweredRate,
    unansweredRateChange: change(unansweredRate, previousUnansweredRate),
    toolErrorRate: percent(counts.toolErrorTurns, counts.aiTurns),
    avgAiLatencySeconds: counts.aiTurns ? counts.aiLatencyMsTotal / counts.aiTurns / 1000 : undefined,
    costPerConversation: counts.aiConversations ? counts.aiCostUsd / counts.aiConversations : undefined,
    avgStaffResponseMinutes: counts.staffResponses
      ? counts.staffResponseMinutesTotal / counts.staffResponses
      : undefined,
  }
}

/** Sums counts across rows and derives weighted rates (total ÷ total), never an average of row rates. */
export function totalCounts(rows: SmsAgentOpsCounts[]): SmsAgentOpsCounts & SmsAgentOpsRates {
  const totals = { ...EMPTY_COUNTS }
  for (const row of rows) {
    for (const key of COUNT_KEYS) totals[key] += row[key]
  }
  return { ...totals, ...ratesFor(totals) }
}

/** What the Getting Worse section compares against the previous period (plan D14). */
export type WorseningMetric = `takeoverRate` | `unansweredRate`

const CHANGE_KEY: Record<WorseningMetric, `takeoverRateChange` | `unansweredRateChange`> = {
  takeoverRate: `takeoverRateChange`,
  unansweredRate: `unansweredRateChange`,
}

/** Locations whose rate rose versus the previous period (any rise), biggest rise first. */
export function worseningLocations<T extends SmsAgentOpsCounts>(rows: T[], metric: WorseningMetric): T[] {
  return rows
    .map((row) => ({ row, delta: ratesFor(row)[CHANGE_KEY[metric]] }))
    .filter((entry): entry is { row: T; delta: number } => entry.delta !== undefined && entry.delta > 0)
    .sort((a, b) => b.delta - a.delta)
    .map(({ row }) => row)
}

export type SmsWorkflowRates = { replyRate?: number; optOutRate?: number; failureRate?: number }

/** Reply and opt-out rates per threads texted; failure rate per texts sent. */
export function workflowRatesFor(row: SmsWorkflowRow): SmsWorkflowRates {
  return {
    replyRate: percent(row.repliedThreads, row.threads),
    optOutRate: percent(row.optOuts, row.threads),
    failureRate: percent(row.failedTexts, row.textsSent),
  }
}
